import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, ButtonBase, Stack, Typography } from '@mui/material';
import {
  APP_STORE_URL,
  BRAND,
  PLAY_STORE_URL,
  detectMobilePlatform,
} from '../../brand/brand';

const AppleGlyph = () => (
  <Box component="svg" width={24} height={24} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <path d="M17.05 12.04c-.02-2.05 1.67-3.04 1.75-3.09-.95-1.39-2.43-1.58-2.96-1.6-1.26-.13-2.46.74-3.1.74-.64 0-1.63-.72-2.68-.7-1.38.02-2.65.8-3.36 2.03-1.43 2.48-.37 6.16 1.03 8.18.68.99 1.5 2.1 2.55 2.06 1.02-.04 1.41-.66 2.65-.66 1.23 0 1.58.66 2.66.64 1.1-.02 1.8-1.01 2.47-2 .78-1.15 1.1-2.27 1.12-2.33-.02-.01-2.14-.82-2.13-3.27zM15.05 5.85c.55-.68.93-1.6.83-2.54-.8.04-1.79.54-2.37 1.2-.51.59-.97 1.55-.85 2.46.91.07 1.83-.46 2.39-1.12z" />
  </Box>
);

const PlayGlyph = () => (
  <Box component="svg" width={24} height={24} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <path d="M3.6 2.3C3.2 2.5 3 2.9 3 3.5v17c0 .6.2 1 .6 1.2L13 12 3.6 2.3zm10.7 10.4l2.7 2.6-11 6.3 8.3-8.9zm0-1.4L6 2.4l11 6.3-2.7 2.6zm6.4-2.2c.7.4.7 1.5 0 1.9l-2.5 1.4-2.9-2.7 2.9-2.7 2.5 2.1z" />
  </Box>
);

const StoreBadge = ({ href, glyph, caption, name, highlighted }) => (
  <ButtonBase
    component="a"
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    aria-label={`${caption} ${name}`}
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 1.5,
      px: 2.25,
      py: 1.25,
      minWidth: 190,
      justifyContent: 'flex-start',
      borderRadius: '14px',
      // The dark badge is the store convention and matches anipaw.net's `.store-btn`.
      backgroundColor: BRAND.ink900,
      color: '#fff',
      border: '1px solid rgba(255,255,255,0.18)',
      boxShadow: highlighted
        ? `0 0 0 3px ${BRAND.purple100}, 0 10px 24px rgba(20,16,30,0.20)`
        : '0 4px 12px rgba(20,16,30,0.14)',
      transition: 'transform .15s, background-color .15s',
      '&:hover': { backgroundColor: '#1F1830', transform: 'translateY(-2px)' },
      '&:focus-visible': { outline: `3px solid ${BRAND.purple400}`, outlineOffset: 2 },
    }}
  >
    {glyph}
    <Box sx={{ textAlign: 'start', lineHeight: 1.15 }}>
      <Typography component="span" sx={{ display: 'block', fontSize: '0.69rem', opacity: 0.75 }}>
        {caption}
      </Typography>
      <Typography
        component="span"
        sx={{ display: 'block', fontSize: '1.05rem', fontWeight: 700, letterSpacing: '-0.01em' }}
      >
        {name}
      </Typography>
    </Box>
  </ButtonBase>
);

/**
 * App Store + Google Play links to the Anipaw app.
 *
 * Both stores are always rendered — a provider may be reading this on a desktop and still need
 * the other platform's link — and the phone's own platform is simply moved first and ringed.
 */
const StoreButtons = ({ platform }) => {
  const { t } = useTranslation();
  const detected = useMemo(() => platform ?? detectMobilePlatform(), [platform]);

  const ios = (
    <StoreBadge
      key="ios"
      href={APP_STORE_URL}
      glyph={<AppleGlyph />}
      caption={t('appLinks.appStoreCaption')}
      name={t('appLinks.appStoreName')}
      highlighted={detected === 'ios'}
    />
  );
  const android = (
    <StoreBadge
      key="android"
      href={PLAY_STORE_URL}
      glyph={<PlayGlyph />}
      caption={t('appLinks.playCaption')}
      name={t('appLinks.playName')}
      highlighted={detected === 'android'}
    />
  );

  return (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      spacing={1.5}
      justifyContent="center"
      sx={{ width: '100%' }}
    >
      {detected === 'android' ? [android, ios] : [ios, android]}
    </Stack>
  );
};

export default StoreButtons;
